import CloseIcon from '@mui/icons-material/Close'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  InputBase,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { MidasConfig } from 'api/midas'
import theme from 'utils/theme'
import MessageBubble, { IncomingBubble, incomingSx } from './MessageBubble'
import { LAUNCHER_LABEL } from './MidasLauncher'
import MarkdownLite from './MarkdownLite'
import { useMidasChat } from './useMidasChat'

export const textButtonSx = {
  height: 'auto',
  minWidth: 0,
  fontSize: '0.875rem',
  textTransform: 'none',
  '&.Mui-disabled': { bgcolor: 'transparent', color: 'text.disabled' }
} as const

// Where the conversation stands, as a caption under the title. It replaced a
// blue banner that sat over the messages (Cam's video, 5 Oct). The "1 to 2
// working days" expectation is in the backend's handover note in the thread.
// Tone of voice v1: short sentences, no dashes, no emoji.
export const AI_ASSISTANT_STATUS = "Goldie's AI assistant"
export const HANDED_OVER_STATUS = 'The Goldie team has this now'
export const CLOSED_STATUS = 'This conversation is closed.'
// The same prompt before and after a handover: the team sees every message.
export const COMPOSER_PLACEHOLDER = 'Ask a question'

// Midas helps first (operator, 6 Oct 2026): there is no one-tap way to a person.
// A customer who wants one asks Midas, which hands over when it should. An older
// backend still serves this quick reply, so it is dropped here.
export function customerQuickReplies(replies: readonly string[]): string[] {
  return replies.filter(
    reply => reply.trim().toLowerCase() !== 'talk to a person'
  )
}

const TITLE_ID = 'midas-panel-title'

interface MidasPanelProps {
  open: boolean
  onClose: () => void
  config: MidasConfig
}

/** Within this many pixels of the end still counts as "at the bottom". */
const BOTTOM_SLACK_PX = 80

export default function MidasPanel({
  open,
  onClose,
  config
}: Readonly<MidasPanelProps>) {
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const chat = useMidasChat(open)
  const [draft, setDraft] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const hasCustomerMessage = chat.messages.some(
    m => m.author_type === 'customer'
  )
  // Not before the history is in: until then a chat the team has would flash
  // up as a new one, with live quick replies and the keyboard up.
  const ready =
    !!chat.conversation && chat.historyLoaded && !chat.error && !chat.closed
  const quickReplies = useMemo(
    () => customerQuickReplies(config.quick_replies),
    [config.quick_replies]
  )
  const lastMessage = chat.messages[chat.messages.length - 1]

  let status = AI_ASSISTANT_STATUS
  if (chat.closed) status = CLOSED_STATUS
  else if (chat.handedOver) status = HANDED_OVER_STATUS

  // Keep the newest message in view as it grows — and when the panel itself resizes. On a
  // phone the on-screen keyboard shrinks the visual viewport, which used to leave the newest
  // message above the fold with the customer having to scroll down again (Greg, 18 Sep).
  // Someone scrolled up reading history is left where they are.
  const atBottom = useRef(true)

  const pinToBottom = useCallback(() => {
    const list = listRef.current
    if (list && atBottom.current) list.scrollTop = list.scrollHeight
  }, [])

  useEffect(() => {
    atBottom.current = true
    pinToBottom()
  }, [chat.messages, open, pinToBottom])

  useEffect(() => {
    if (!open) return
    const list = listRef.current
    const onScroll = () => {
      if (!list) return
      atBottom.current =
        list.scrollTop >=
        list.scrollHeight - list.clientHeight - BOTTOM_SLACK_PX
    }
    list?.addEventListener('scroll', onScroll, { passive: true })
    const viewport = window.visualViewport
    viewport?.addEventListener('resize', pinToBottom)
    window.addEventListener('resize', pinToBottom)
    const observer =
      list && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => pinToBottom())
        : undefined
    if (list) observer?.observe(list)
    return () => {
      list?.removeEventListener('scroll', onScroll)
      viewport?.removeEventListener('resize', pinToBottom)
      window.removeEventListener('resize', pinToBottom)
      observer?.disconnect()
    }
  }, [open, pinToBottom])

  // The composer is disabled while Midas replies; hand focus back after.
  useEffect(() => {
    if (open && ready && !chat.replying) inputRef.current?.focus()
  }, [open, ready, chat.replying])

  // Announce finished replies once, not every streamed chunk.
  const announcement = useMemo(() => {
    const finished = [...chat.messages]
      .reverse()
      .find(
        m =>
          (m.author_type === 'midas' || m.author_type === 'agent') &&
          m.status === 'complete'
      )
    if (!finished) return ''
    const who = finished.author_type === 'agent' ? 'Goldie team' : 'Midas'
    return `${who}: ${finished.body}`
  }, [chat.messages])

  const submit = async (text: string) => {
    if (chat.replying || !ready || !text.trim()) return
    if (await chat.send(text)) setDraft('')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit(draft)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby={TITLE_ID}
      TransitionProps={{
        onEntered: () => {
          if (ready) inputRef.current?.focus()
        }
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(33, 30, 22, 0.12)' } }
      }}
      PaperProps={{
        sx: fullScreen
          ? { display: 'flex', flexDirection: 'column' }
          : {
              position: 'fixed',
              right: 16,
              bottom: 16,
              m: 0,
              width: 400,
              maxWidth: 'calc(100vw - 32px)',
              height: 'min(640px, calc(100vh - 32px))',
              maxHeight: 'none',
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column'
            }
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        gap={1.5}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid #EEE9E0' }}
      >
        <Box flex={1} minWidth={0}>
          <Typography id={TITLE_ID} component='h2' fontWeight={700}>
            {LAUNCHER_LABEL}
          </Typography>
          {/* Read out when it changes, as the banner it replaced was. */}
          <Box aria-live='polite'>
            <Typography variant='caption' color='text.secondary' component='p'>
              {status}
            </Typography>
            {chat.handedOver && config.support_hours && (
              <Typography
                variant='caption'
                color='text.secondary'
                component='p'
              >
                {`Support hours: ${config.support_hours}`}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton aria-label='Close chat' onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack
        ref={listRef}
        role='log'
        aria-live='off'
        aria-label='Conversation with Midas'
        gap={1.5}
        sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}
      >
        {/* The welcome text is the backend's (GET /midas/config), as set in
            admin. It opens the thread, so it waits for the thread. */}
        {chat.historyLoaded && (
          <IncomingBubble label='Midas'>
            <Box sx={incomingSx}>
              <MarkdownLite text={config.welcome_message} />
            </Box>
          </IncomingBubble>
        )}

        {ready && !hasCustomerMessage && !chat.handedOver && (
          <Stack direction='row' flexWrap='wrap' gap={1}>
            {quickReplies.map(reply => (
              <Chip
                key={reply}
                label={reply}
                variant='outlined'
                color='primary'
                onClick={() => submit(reply)}
                disabled={chat.replying}
              />
            ))}
          </Stack>
        )}

        {chat.messages.map(message => (
          <MessageBubble
            key={message.id}
            message={message}
            onFeedback={chat.giveFeedback}
            onRetry={
              message === lastMessage && chat.lastCustomerBody
                ? () => submit(chat.lastCustomerBody ?? '')
                : undefined
            }
          />
        ))}

        {chat.loading && (
          <Typography variant='caption' color='text.secondary'>
            Connecting…
          </Typography>
        )}

        {chat.error && (
          <Alert
            severity='error'
            action={
              <Button
                variant='text'
                size='small'
                onClick={chat.retry}
                sx={textButtonSx}
              >
                Retry
              </Button>
            }
          >
            Midas couldn&apos;t connect just now.
          </Alert>
        )}

        {/* The header says the conversation is closed. */}
        {chat.closed && (
          <Button
            variant='text'
            size='small'
            onClick={chat.startNewChat}
            sx={{ ...textButtonSx, alignSelf: 'center' }}
          >
            Start a new chat
          </Button>
        )}

        {chat.sendError && (
          <Alert severity='error' role='alert'>
            {chat.sendError}
          </Alert>
        )}
      </Stack>

      <Box
        aria-live='polite'
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)'
        }}
      >
        {announcement}
      </Box>

      <Box sx={{ borderTop: '1px solid #EEE9E0', px: 2, pt: 1, pb: 1.5 }}>
        <Stack direction='row' alignItems='flex-end' gap={1}>
          <InputBase
            inputRef={inputRef}
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={COMPOSER_PLACEHOLDER}
            // The phone keyboard's return key reads "send": in Cam's video
            // (5 Oct) he tapped Done and "Respond please" never sent.
            inputProps={{ 'aria-label': 'Message', enterKeyHint: 'send' }}
            disabled={!ready || chat.replying}
            multiline
            maxRows={5}
            sx={{
              flex: 1,
              bgcolor: '#F7F5F0',
              borderRadius: 5,
              px: 2,
              py: 1,
              fontSize: '0.9375rem'
            }}
          />
          <IconButton
            aria-label='Send'
            color='primary'
            onClick={() => submit(draft)}
            disabled={!ready || chat.replying || !draft.trim()}
          >
            <SendRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    </Dialog>
  )
}
